import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "@/components/Icon";
import { Screen, Header } from "@/components/Layout";
import { Spinner } from "@/components/Controls";
import { colors, spacing, radius, fonts, type } from "@/theme";
import {
  getFamilyAccountToken, familyAccountMe, familyAccountLogin, familyAccountRegister,
  familyAccountLink, familyAccountOpenSession, familyAccountLogout, familyAccountDelete,
  paymentStatus, createPaypalOrder, capturePaypalOrder,
} from "@/api";

export default function FamilyAccount() {
  const navigate = useNavigate();
  const [booting, setBooting] = useState(true);
  const [account, setAccount] = useState(null);
  const [linked, setLinked] = useState([]);
  const [payment, setPayment] = useState(null);
  const [upgrading, setUpgrading] = useState(false);

  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState(null);

  const [code, setCode] = useState("");
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkError, setLinkError] = useState(null);
  const [openingId, setOpeningId] = useState(null);

  const refresh = useCallback(async () => {
    const token = getFamilyAccountToken();
    if (!token) { setAccount(null); setBooting(false); return; }
    const me = await familyAccountMe();
    if (me) {
      setAccount(me.account);
      setLinked(me.linked);
      setPayment(await paymentStatus());
    } else {
      setAccount(null);
    }
    setBooting(false);
  }, []);

  useEffect(() => {
    // Capture a PayPal return (?paypal=success&token=ORDERID) if present.
    const params = new URLSearchParams(window.location.search);
    if (params.get("paypal") === "success" && params.get("token")) {
      (async () => {
        await capturePaypalOrder(params.get("token"));
        window.history.replaceState(null, "", window.location.pathname);
        refresh();
      })();
    } else {
      refresh();
    }
  }, [refresh]);

  const submitAuth = async () => {
    setAuthError(null); setAuthBusy(true);
    const res = mode === "login"
      ? await familyAccountLogin(email.trim(), password)
      : await familyAccountRegister(email.trim(), password, name.trim() || undefined);
    setAuthBusy(false);
    if (!res.ok) { setAuthError(res.error); return; }
    setPassword("");
    await refresh();
  };

  const signInWithGoogle = () => {
    setAuthError(null);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirect = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  const upgradeToPremium = async () => {
    if (upgrading) return;
    setUpgrading(true);
    try {
      const base = window.location.origin + "/family-account";
      const order = await createPaypalOrder(`${base}?paypal=success`, `${base}?paypal=cancel`);
      if (!order.ok || !order.approve_url) {
        window.alert(order.reason === "no_key" ? "Payments aren't configured yet. Please ask your app helper to add PayPal keys." : "We couldn't start the upgrade. Please try again.");
        return;
      }
      window.location.href = order.approve_url;
    } finally {
      setUpgrading(false);
    }
  };

  const submitLink = async () => {
    setLinkError(null); setLinkBusy(true);
    const res = await familyAccountLink(code.trim().toUpperCase());
    setLinkBusy(false);
    if (!res.ok) { setLinkError(res.error); return; }
    setCode("");
    await refresh();
  };

  const openLovedOne = async (lo) => {
    setOpeningId(lo.user_id);
    const session = await familyAccountOpenSession(lo.user_id);
    setOpeningId(null);
    if (!session) { setLinkError("Couldn't open the portal right now. Please try again."); return; }
    navigate("/family-portal", { state: { userId: lo.user_id, firstName: lo.first_name, token: session.family_token } });
  };

  const signOut = async () => { await familyAccountLogout(); setAccount(null); setLinked([]); setEmail(""); setPassword(""); };
  const deleteAccount = async () => {
    if (!window.confirm("Delete your account?\n\nThis permanently deletes your family account and saved loved-one links. This cannot be undone.")) return;
    await familyAccountDelete(); setAccount(null); setLinked([]); setPayment(null); setEmail(""); setPassword("");
  };

  if (booting) {
    return <Screen testID="family-account-booting" maxWidth={640}><Header title="Family account" /><div style={{ display: "flex", justifyContent: "center", padding: spacing["3xl"] }}><Spinner size={40} /></div></Screen>;
  }

  if (account) {
    return (
      <Screen testID="family-account-signedin" maxWidth={640}>
        <Header title="Family account" />
        <div style={{ padding: `${spacing.xl}px ${spacing.lg}px ${spacing["3xl"]}px` }}>
          <div style={{ display: "flex", alignItems: "center", gap: spacing.md }}>
            {account.photo ? (
              <img src={account.photo} alt="" style={{ width: 52, height: 52, borderRadius: 26, objectFit: "cover" }} />
            ) : (
              <div style={{ width: 52, height: 52, borderRadius: 26, background: colors.brandTertiary, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{ ...fonts.displayBold, fontSize: type.xl, color: colors.brand }}>{(account.display_name || account.email || "?").charAt(0).toUpperCase()}</span>
              </div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ ...fonts.displayBold, fontSize: type.xl, color: colors.onSurface }}>Welcome{account.display_name ? `, ${account.display_name.split(" ")[0]}` : " back"}</div>
              <div style={{ ...fonts.text, fontSize: type.base, color: colors.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{account.email}</div>
            </div>
          </div>
          <p style={{ ...fonts.text, fontSize: type.lg, color: colors.muted, marginTop: spacing.xs, marginBottom: spacing.xl, lineHeight: 1.4 }}>Choose a loved one to manage their familiar voice.</p>

          <div data-testid="premium-card" style={{ padding: spacing.lg, borderRadius: radius.lg, border: `1px solid ${account.is_premium ? colors.brandSecondary : colors.border}`, background: account.is_premium ? colors.brandTertiary : colors.surfaceSecondary, marginBottom: spacing.xl }}>
            <div style={{ display: "flex", alignItems: "center", gap: spacing.sm, marginBottom: spacing.sm }}>
              <Icon name={account.is_premium ? "award" : "star"} size={22} color={account.is_premium ? colors.brand : colors.brandSecondary} />
              <span style={{ ...fonts.displaySemiBold, fontSize: type.xl, color: colors.onSurface }}>{account.is_premium ? "Premium active" : "Ember Premium"}</span>
            </div>
            {account.is_premium ? (
              <div style={{ ...fonts.text, fontSize: type.base, color: colors.onSurface, lineHeight: 1.4 }}>Thank you for supporting your loved one. Familiar Voice and text updates are unlocked.</div>
            ) : (
              <>
                <div style={{ ...fonts.text, fontSize: type.base, color: colors.onSurface, lineHeight: 1.4 }}>
                  Unlock the Familiar Voice (a cloned voice of someone they love) and text-message updates{payment ? ` — a one-time ${payment.currency === "USD" ? "$" : ""}${payment.price}${payment.currency !== "USD" ? " " + payment.currency : ""}.` : "."}
                </div>
                <button data-testid="upgrade-premium-button" onClick={upgradeToPremium} disabled={upgrading} className="ember-press" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: spacing.sm, minHeight: 52, width: "100%", borderRadius: radius.lg, background: colors.brandPrimary, marginTop: spacing.md }}>
                  {upgrading ? <Spinner color={colors.onBrandPrimary} /> : <><Icon name="credit-card" size={18} color={colors.onBrandPrimary} /><span style={{ ...fonts.textExtraBold, fontSize: type.base, color: colors.onBrandPrimary }}>Upgrade with PayPal</span></>}
                </button>
              </>
            )}
          </div>

          {linked.length === 0 ? (
            <div data-testid="account-empty" style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: `${spacing.xl}px 0`, gap: spacing.md }}>
              <Icon name="user-plus" size={44} color={colors.border} />
              <div style={{ ...fonts.textSemiBold, fontSize: type.lg, color: colors.muted, textAlign: "center", padding: `0 ${spacing.lg}px`, lineHeight: 1.5 }}>No loved ones linked yet. Enter the 8-character code from their app below.</div>
            </div>
          ) : (
            linked.map((lo) => (
              <button key={lo.user_id} data-testid={`loved-one-${lo.user_id}`} onClick={() => openLovedOne(lo)} disabled={openingId === lo.user_id} className="ember-press"
                style={{ display: "flex", alignItems: "center", gap: spacing.md, width: "100%", textAlign: "left", background: colors.surfaceSecondary, borderRadius: radius.lg, padding: spacing.lg, marginBottom: spacing.md }}>
                <div style={{ width: 52, height: 52, borderRadius: 26, background: colors.brandTertiary, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ ...fonts.displayBold, fontSize: type.xl, color: colors.brand }}>{(lo.first_name || "?").charAt(0).toUpperCase()}</span>
                </div>
                <span style={{ flex: 1, ...fonts.displaySemiBold, fontSize: type.xl, color: colors.onSurface }}>{lo.first_name}</span>
                {openingId === lo.user_id ? <Spinner size={22} /> : <Icon name="chevron-right" size={26} color={colors.muted} />}
              </button>
            ))
          )}

          <div style={{ marginTop: spacing.lg, padding: spacing.lg, borderRadius: radius.lg, border: `1px solid ${colors.border}`, background: colors.surface }}>
            <div style={{ ...fonts.textBold, fontSize: type.lg, color: colors.onSurface, marginBottom: spacing.md }}>Link a loved one</div>
            <input data-testid="account-code-input" value={code} maxLength={8} onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))} placeholder="ABCD2345"
              style={{ ...fonts.displayBold, fontSize: 30, letterSpacing: 6, color: colors.onSurface, textAlign: "center", background: colors.surfaceSecondary, borderRadius: radius.lg, border: `2px solid ${colors.border}`, padding: `${spacing.md}px 0`, width: "100%" }} />
            {linkError && <div style={errText}>{linkError}</div>}
            <button data-testid="account-link-button" disabled={code.length !== 8 || linkBusy} onClick={submitLink} className="ember-press" style={{ ...cta, background: code.length !== 8 ? colors.border : colors.brandPrimary }}>
              {linkBusy ? <Spinner color={colors.onBrandPrimary} /> : <span style={ctaText}>Link loved one</span>}
            </button>
          </div>

          <button data-testid="account-signout" onClick={signOut} className="ember-press" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: spacing.sm, minHeight: 48, width: "100%", marginTop: spacing.xl }}>
            <Icon name="log-out" size={20} color={colors.error} /><span style={{ ...fonts.textBold, fontSize: type.base, color: colors.error }}>Sign out</span>
          </button>
          <button data-testid="account-delete" onClick={deleteAccount} style={{ minHeight: 44, width: "100%", marginTop: spacing.sm }}>
            <span style={{ ...fonts.textSemiBold, fontSize: type.base, color: colors.muted, textDecoration: "underline" }}>Delete my account</span>
          </button>
        </div>
      </Screen>
    );
  }

  return (
    <Screen testID="family-account-auth" maxWidth={520}>
      <Header title="Family account" />
      <div style={{ padding: spacing.xl, display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div style={{ width: 84, height: 84, borderRadius: 42, background: colors.brandTertiary, display: "flex", alignItems: "center", justifyContent: "center", marginTop: spacing.lg, marginBottom: spacing.lg }}>
          <Icon name="users" size={38} color={colors.brand} />
        </div>
        <div style={{ ...fonts.displayBold, fontSize: type["2xl"], color: colors.onSurface, marginBottom: spacing.sm, textAlign: "center" }}>{mode === "login" ? "Welcome back" : "Create your account"}</div>
        <div style={{ ...fonts.text, fontSize: type.lg, color: colors.muted, textAlign: "center", lineHeight: 1.5, marginBottom: spacing.xl }}>
          A family account lets you manage a loved one&rsquo;s familiar voice without re-entering their code each time.
        </div>

        {mode === "register" && <input data-testid="account-name-input" style={field} placeholder="Your name (optional)" value={name} onChange={(e) => setName(e.target.value)} />}
        <input data-testid="account-email-input" style={field} placeholder="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <input data-testid="account-password-input" style={field} placeholder={mode === "register" ? "Password (min 8 characters)" : "Password"} type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
        {authError && <div style={{ ...errText, alignSelf: "stretch", textAlign: "center" }}>{authError}</div>}

        <button data-testid="account-submit" disabled={!email || !password || authBusy} onClick={submitAuth} className="ember-press" style={{ ...cta, background: (!email || !password) ? colors.border : colors.brandPrimary }}>
          {authBusy ? <Spinner color={colors.onBrandPrimary} /> : <span style={ctaText}>{mode === "login" ? "Sign in" : "Create account"}</span>}
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: spacing.md, width: "100%", margin: `${spacing.lg}px 0` }}>
          <div style={{ flex: 1, height: 1, background: colors.border }} /><span style={{ ...fonts.textSemiBold, fontSize: type.base, color: colors.muted }}>or</span><div style={{ flex: 1, height: 1, background: colors.border }} />
        </div>

        <button data-testid="account-google" onClick={signInWithGoogle} className="ember-press"
          style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: spacing.md, minHeight: 56, width: "100%", borderRadius: radius.lg, border: `2px solid ${colors.border}`, background: colors.surface }}>
          <div style={{ width: 26, height: 26, borderRadius: 13, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", border: `1px solid ${colors.border}` }}>
            <span style={{ ...fonts.displayBold, fontSize: 18, color: "#4285F4" }}>G</span>
          </div>
          <span style={{ ...fonts.textExtraBold, fontSize: type.base, color: colors.onSurface }}>Continue with Google</span>
        </button>

        <button data-testid="account-toggle-mode" onClick={() => { setMode(mode === "login" ? "register" : "login"); setAuthError(null); }} style={{ minHeight: 44, marginTop: spacing.md }}>
          <span style={{ ...fonts.textBold, fontSize: type.base, color: colors.brand }}>{mode === "login" ? "New here? Create an account" : "Already have an account? Sign in"}</span>
        </button>
      </div>
    </Screen>
  );
}

const field = { ...fonts.textSemiBold, fontSize: type.lg, color: colors.onSurface, background: colors.surfaceSecondary, borderRadius: radius.lg, border: `2px solid ${colors.border}`, padding: `${spacing.md}px ${spacing.lg}px`, width: "100%", minHeight: 56, marginBottom: spacing.md };
const cta = { display: "flex", alignItems: "center", justifyContent: "center", minHeight: 60, borderRadius: radius.lg, width: "100%", marginTop: spacing.lg };
const ctaText = { ...fonts.textExtraBold, fontSize: type.lg, color: colors.onBrandPrimary };
const errText = { ...fonts.textSemiBold, fontSize: type.base, color: colors.error, marginTop: spacing.md };
